
import {useMemo} from 'react';
import {gql, useQuery} from '@apollo/client';
import {sha1} from 'object-hash';
import {useQueryValues} from 'utils/queryObj';

import {backgrounds, FactoidConfig, ScreenConfig} from './types';

type Factoid = {
	id: string
	text: string
	illustration: {
		id: string
		name: string
		layout: 'Top' | 'Bottom' | 'Either'
		url: string
	}
}

interface UserResult {
	auth: {
		user?: {
			id: string
			dashboard: {
				firstLook?: {
					facts: Factoid[]
				}
			}
		}
	}
}

const userFirstLook = gql`
query {
    auth {
		id
        user {
			id

            dashboard {
				# do NOT put ID here as it
				# triggers cache invalidation
                firstLook(cacheOnly: true)
            }
        }
    }
}`;

interface AdminResult {
	nested: {
		user?: {
			dashboard: {
				firstLook?: {
					facts: Factoid[]
				}
			}
		}
	}
}

const adminFirstLook = gql`
query ($userId: ID!) {
	nested {
		user(id: $userId) {
			id

            dashboard {
				# do NOT put ID here as it
				# triggers cache invalidation
                firstLook(cacheOnly: false)
            }
		}
	}
}`;

export const useDynamicQuery = (override?: string) => {
	if (override) {
		const {data} = useQuery<AdminResult>(adminFirstLook, {
			variables: {
				userId: override,
			},
			context: {
				slowQuery: true,
			},
		});

		const facts = data?.nested.user?.dashboard.firstLook?.facts;
		return {
			facts,
			user: undefined,
		};
	}

	const {data} = useQuery<UserResult>(userFirstLook);
	const facts = data?.auth.user?.dashboard.firstLook?.facts;
	return {
		facts,
		user: data?.auth.user?.id,
	};
};

export const useScreens = (): ScreenConfig[] => {
	const {read} = useQueryValues('fl');
	const {id: override} = read({id: ''});

	const {facts, user} = useDynamicQuery(override);
	return useMemo(() => {
		if (!facts) {
			return [];
		}

		return [
			{
				type: 'intro',
				id: 'intro',
			},

			...getFactoids(facts),

			{
				type: 'info',
				id: 'info-1',
				props: {
					image: 'https://static.bodyspec.com/n8n/fl/fcea4086157be7aec6f1a82a01210a14',
					text: '**Want a deeper dive?**\n\nTry out our AI voice consultant to get a personalized explainer!\n\nAccess it directly from the dashboard.',
				},
			},

			{
				type: 'info',
				id: 'info-2',
				props: {
					image: 'https://static.bodyspec.com/n8n/fl/85528ac7f81e3e3d776d42d7ea3cd699',
					text: 'Speak naturally! The more specific your questions are, the more helpful its responses can be.',
				},
			},

			{
				type: 'feedback',
				id: 'feedback',
				bg: getBackground({}),
				props: {user},
			},
		];
	}, [facts]);
};

const hashIdx = (ref: object): number => {
	const hash = sha1(ref);
	return parseInt(hash.slice(0, 8), 16);
};

const getLayout = (fact: Factoid, index: number): 'Top' | 'Bottom' => {
	if (fact.illustration.layout === 'Either') {
		const even = hashIdx({fact, index}) % 2 === 0;
		return even ? 'Top' : 'Bottom';
	}
	return fact.illustration.layout;
};

const getBackground = (ref: object) => {
	const idx = hashIdx(ref);
	return backgrounds[idx % backgrounds.length];
};

const getFactoids = (facts: Factoid[]): FactoidConfig[] => (
	facts.map((fact, index) => {
		// Generate a basic factoid from the GQL input
		const factoid: FactoidConfig = {
			id: `${fact.id}-${index}`,
			type: 'factoid',
			bg: getBackground({fact, index}),

			text: fact.text,
			illustration: {
				id: fact.illustration.id,
				name: fact.illustration.name,
				layout: getLayout(fact, index),
				url: fact.illustration.url,
			},
		};
		return factoid;
	}).map((fact, index, arr) => {
		// Update background in a random but repeatable way
		// to avoid duplicating the previous entry
		if (index > 0) {
			const prev = arr[index - 1];
			let incr = 0;
			while (fact.bg === prev.bg) {
				fact.bg = getBackground({
					fact,
					index,
					incr,
				});
				incr++;
			}
		}
		return fact;
	})
);
